import React, { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, UtensilsCrossed, ShoppingBag, Map, Briefcase, Play, Pause, ChevronUp, X, type LucideIcon } from "lucide-react";
import LanguageSheetHost from "./LanguageSheet";
import { useTranslation } from "react-i18next";
import { useAudioTour } from "../contexts/AudioTourContext";
import { ScrollRestorer } from "../hooks/useScrollRestoration";
import DeliveryWidget from "./DeliveryWidget";
import { useStatusBarRoute } from "../lib/statusBar";
import { useGuestBookingReference, useGuestToken } from "../hooks/useGuestSession";
import { useCart } from "../lib/cart";
import { useUnreadBadge } from "../lib/unreadBadge";
import "./TabBar.css";
import { logNavigation } from "../lib/tapLog";

/** Il numerino: oltre il 9 basta dire che sono tanti. */
const badgeText = (count: number): string => (count > 9 ? "9+" : String(count));

const Layout = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const location = useLocation();
  // Registro dei tocchi (lib/tapLog): ogni cambio di pagina.
  useEffect(() => {
    logNavigation(location.pathname + location.search);
  }, [location.pathname, location.search]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isIOS = useRef(/iPad|iPhone|iPod/.test(navigator.userAgent));

  // Cambiando percorso la barra di stato prende il colore della pagina nuova
  // (lib/statusBar), prima che si veda.
  useStatusBarRoute(location.pathname, location.search);

  useEffect(() => {
    if (isIOS.current) {
      audioRef.current = new Audio('/sounds/click.wav');
      audioRef.current.preload = 'auto';
      for (let i = 0; i < 3; i++) {
        const audio = new Audio('/sounds/click.wav');
        audio.preload = 'auto';
        audio.load();
      }
    } else {
      audioRef.current = new Audio('/sounds/click.wav');
      audioRef.current.preload = 'auto';
    }

    const preloadAudio = () => {
      if (audioRef.current) {
        audioRef.current.load();
      }
    };

    document.addEventListener('touchstart', preloadAudio, { once: true });
    document.addEventListener('click', preloadAudio, { once: true });

    return () => {
      document.removeEventListener('touchstart', preloadAudio);
      document.removeEventListener('click', preloadAudio);
      audioRef.current = null;
    };
  }, []);

  const handleNavClick = () => {
    if (isIOS.current) {
      const audio = new Audio('/sounds/click.wav');
      audio.play().catch(() => {});
    } else if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  };

  // I numerini, da quello che l'app sa gia' (nessuna richiesta in piu'):
  // gli extra nel carrello (lib/cart, della prenotazione in sessione) e i
  // messaggi della reception non letti (lib/unreadBadge, li conta
  // HomeSessionHost). A zero non si vedono.
  const bookingReference = useGuestBookingReference();
  const guestToken = useGuestToken();
  const cartCount = useCart(bookingReference).count;
  const unreadCount = useUnreadBadge(guestToken);

  const navItems: Array<{
    path: string;
    labelKey: string;
    icon: LucideIcon;
    also?: string[];
    badge?: number;
    badgeLabel?: string;
  }> = [
    { path: "/", labelKey: "navigation.home", icon: Home },
    // Ristoranti e partner stanno nella stessa scheda (si sceglie in cima
    // alla pagina): il posto liberato va agli Extra, che si comprano.
    { path: "/restaurants", labelKey: "navigation.places", icon: UtensilsCrossed, also: ["/partners"] },
    { path: "/extras", labelKey: "navigation.extras", icon: ShoppingBag, also: ["/extras/"],
      badge: cartCount, badgeLabel: t("navigation.cartBadge", { count: cartCount }) },
    { path: "/explore", labelKey: "navigation.explore", icon: Map },
    { path: "/my-stay", labelKey: "navigation.myStay", icon: Briefcase,
      badge: unreadCount, badgeLabel: t("home.reception.unread", { count: unreadCount }) },
  ];

  const isActive = (item: { path: string; also?: string[] }) =>
    location.pathname === item.path
    || (item.also || []).some((p) => (p.endsWith('/') ? location.pathname.startsWith(p) : location.pathname === p));

  // Il contenitore fisso non ha un fondo suo: sotto la barra di stato si
  // vede lo sfondo della pagina (html/body, lo scrive lib/statusBar), che
  // Safari 26+ usa per colorare la barra. Il fondo dell'app (chiaro e scuro)
  // lo dipinge la parte sotto, che non è fissa.
  // Il margine in cima (--nva-top-gap) c'è solo con la striscia a tinta unita
  // delle prove (pannello, lib/statusBar): di serie è 0 ovunque. Nell'app
  // installata su iPhone vale lo stile nativo: le testate partono da 0 sotto
  // la sfumatura di iOS e solo il loro contenuto scende di 10px.
  return (
    <div className="fixed inset-0 flex flex-col" style={{ paddingTop: 'var(--nva-top-gap, 0px)' }}>
      {/* La consegna in corso (caffè, vino, scorte): una riga in cima a ogni
          pagina, nel flusso: occupa il suo spazio e il contenuto parte sotto.
          Sta qui perché il Layout è l'unico pezzo presente in tutte le pagine. */}
      <DeliveryWidget />

      <div className="flex-1 relative bg-gray-50 dark:bg-gray-900">
        {children}
        {/* Un guardiano solo per tutta l'app: rimette lo scorrimento dov'era
            quando si torna indietro. Sta qui perché il Layout non si rimonta. */}
        <ScrollRestorer />
      </div>

      {/* La barra in basso (TabBar.css): una capsula di vetro sospesa
          nello spazio di sempre (72 px + zona sicura). La scheda attiva e'
          la pillola espresso; nelle pagine dei servizi nessuna è attiva. */}
      <nav className="nva-tabbar" aria-label={t("navigation.tabBar")}>
        <div className="nva-tabbar__capsule">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            const label = t(item.labelKey);
            const badge = item.badge && item.badge > 0 ? item.badge : 0;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className="nva-tabbar__tab"
                aria-current={active ? "page" : undefined}
                aria-label={badge ? `${label}, ${item.badgeLabel}` : label}
              >
                <span className="nva-tabbar__icon">
                  <Icon className="h-6 w-6" strokeWidth={active ? 2.3 : 2} aria-hidden="true" />
                  {badge ? (
                    <span className="nva-tabbar__badge" aria-hidden="true">{badgeText(badge)}</span>
                  ) : null}
                </span>
                <span className="nva-tabbar__label">{label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Audio Tour Mini-Player (visible when minimized, across all pages) */}
      <AudioTourMiniPlayer />

      {/* Il foglio della lingua, una volta sola e fuori da menu e barre. */}
      <LanguageSheetHost />
    </div>
  );
};

const AudioTourMiniPlayer: React.FC = () => {
  const navigate = useNavigate();
  const { isActive, isMinimized, isPlaying, currentName, currentImage, stopLabel,
    tour, togglePlay, expand, stopTour } = useAudioTour();

  if (!isActive || !isMinimized || !tour) return null;

  return (
    <div
      className="fixed z-[60] left-3 right-3"
      style={{ bottom: 'calc(80px + env(safe-area-inset-bottom))' }}
    >
      <div className="bg-gray-900/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl shadow-black/50 px-4 py-3 flex items-center gap-3">
        {/* Thumbnail */}
        <div
          className="w-11 h-11 rounded-xl bg-cover bg-center flex-shrink-0 border border-white/10"
          style={{ backgroundImage: `url(${currentImage})` }}
        />
        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold truncate">{currentName}</p>
          <p className="text-white/50 text-xs truncate">{tour.cityName} · {stopLabel}</p>
        </div>
        {/* Play/Pause */}
        <button
          onClick={togglePlay}
          className="w-9 h-9 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0"
        >
          {isPlaying ? (
            <Pause className="h-4 w-4 text-white" fill="white" />
          ) : (
            <Play className="h-4 w-4 text-white ml-0.5" fill="white" />
          )}
        </button>
        {/* Expand */}
        <button
          onClick={() => { expand(); navigate(`/audio-tour/${tour.citySlug}`); }}
          className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0"
        >
          <ChevronUp className="h-4 w-4 text-white" />
        </button>
        {/* Close */}
        <button
          onClick={stopTour}
          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
        >
          <X className="h-3.5 w-3.5 text-white/40" />
        </button>
      </div>
    </div>
  );
};

export default Layout;